import { supabase } from '@/integrations/supabase/client';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { Contrato, ContratoWithDetails, PropostaParaContrato } from '@/types/contrato';

export class ContratosService {
  /**
   * Busca propostas que podem ser convertidas em contratos usando autenticação RBAC
   * (propostas com PDF gerado)
   */
  static async getPropostasParaContrato(): Promise<PropostaParaContrato[]> {
    try {
      console.log('🔍 Buscando propostas para contrato via API backend com RBAC...');

      // Usar fetchWithAuth para respeitar políticas RBAC
      const response = await fetchWithAuth('/api/contratos/propostas-disponiveis', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Erro ao carregar propostas: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ ${data?.length || 0} propostas carregadas com sucesso (filtradas por RBAC)`);
      
      return data as PropostaParaContrato[];
    } catch (error) {
      console.error('❌ Erro ao carregar propostas para contrato:', error);
      throw error;
    }
  }

  /**
   * Deleta o PDF existente do contrato (storage + coluna) e reseta status
   */
  static async deletarPdfContrato(contratoId: string, pdfUrl: string): Promise<void> {
    try {
      if (!pdfUrl) return;

      // Extrair o caminho do arquivo a partir da URL pública
      const bucketName = 'contratos';
      const marker = `/storage/v1/object/public/${bucketName}/`;
      const parts = pdfUrl.split(marker);
      if (parts.length < 2) {
        console.warn('URL de PDF inválida, não foi possível extrair o caminho:', pdfUrl);
      } else {
        const filePath = parts[1];
        // Remover do storage
        const { error: removeError } = await supabase
          .storage
          .from(bucketName)
          .remove([filePath]);
        if (removeError) {
          console.warn('Erro ao remover PDF do storage (seguindo adiante para limpar coluna):', removeError);
        }
      }

      // Limpar URL no registro do contrato e retornar status para rascunho
      const { error: updateError } = await supabase
        .from('contratos_sun7')
        .update({ contrato_pdf_url: null, status: 'rascunho' })
        .eq('id', contratoId);

      if (updateError) {
        throw updateError;
      }
    } catch (error) {
      console.error('Erro ao deletar PDF do contrato:', error);
      throw error;
    }
  }

  /**
   * Cria um novo contrato a partir de uma proposta via API backend (evita CORS e aplica RBAC)
   */
  static async criarContrato(propostaId: string, dadosContrato: Partial<Contrato>): Promise<Contrato> {
    try {
      const response = await fetchWithAuth('/api/contratos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          propostaId,
          lead_id: dadosContrato.lead_id,
          ...dadosContrato,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        const err = new Error(errorData.error || errorData.details || `Erro ao criar contrato: ${response.status}`);
        (err as any).details = errorData.details;
        (err as any).code = errorData.code;
        throw err;
      }

      const data = await response.json();
      return data as Contrato;
    } catch (error) {
      console.error('Erro no serviço de contratos:', error);
      throw error;
    }
  }

  /**
   * Atualiza um contrato existente
   */
  static async atualizarContrato(contratoId: string, dadosContrato: Partial<Contrato>): Promise<Contrato> {
    try {
      const { data, error } = await supabase
        .from('contratos_sun7')
        .update(dadosContrato)
        .eq('id', contratoId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar contrato:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro no serviço de contratos:', error);
      throw error;
    }
  }

  /**
   * Marca proposta como aceita
   */
  static async aceitarProposta(contratoId: string): Promise<Contrato> {
    try {
      const { data, error } = await supabase
        .from('contratos_sun7')
        .update({
          proposta_aceita: true,
          data_proposta_aceita: new Date().toISOString(),
          status: 'proposta_aceita'
        })
        .eq('id', contratoId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao aceitar proposta:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro no serviço de contratos:', error);
      throw error;
    }
  }

  /**
   * Busca contrato com detalhes completos
   */
  static async getContratoComDetalhes(contratoId: string): Promise<ContratoWithDetails | null> {
    try {
      console.log('🔍 Buscando contrato com detalhes:', contratoId);
      
      // Buscar dados do contrato
      const { data: contrato, error: contratoError } = await supabase
        .from('contratos_sun7')
        .select('*')
        .eq('id', contratoId)
        .single();

      if (contratoError) {
        console.error('❌ Erro ao buscar contrato:', contratoError);
        throw contratoError;
      }

      if (!contrato) {
        console.log('⚠️ Contrato não encontrado');
        return null;
      }

      // Buscar dados do lead
      const { data: lead, error: leadError } = await supabase
        .from('leads')
        .select(`
          id,
          nome,
          razao_social,
          tipo,
          cpf,
          cnpj,
          representante_legal,
          cpf_representante,
          endereco,
          numero,
          cidade,
          estado,
          cep,
          telefone,
          email
        `)
        .eq('id', contrato.lead_id)
        .single();

      if (leadError) {
        console.error('❌ Erro ao buscar lead:', leadError);
        throw leadError;
      }

      // Buscar dados da proposta
      const { data: proposta, error: propostaError } = await supabase
        .from('propostas_clientes')
        .select(`
          id,
          valor_total_kits,
          preco_integracao,
          potencia_total_paineis,
          potencia_total_inversores,
          total_paineis,
          proposta_pdf_url,
          comissao_vendedor_valor,
          comissao_parceiro_valor,
          comissao_parceiro_pai_valor,
          mao_obra,
          valor_estrutura_metallica,
          custo_total_implantacao,
          custo_total_integracao,
          lucro_liquido
        `)
        .eq('id', contrato.proposta_id)
        .single();

      if (propostaError) {
        console.error('❌ Erro ao buscar proposta:', propostaError);
        throw propostaError;
      }

      // Normaliza o tipo do lead para 'PF' | 'PJ'
      const leadNormalizado = lead
        ? { ...lead, tipo: (lead.tipo === 'fisica' ? 'PF' : 'PJ') as 'PF' | 'PJ' }
        : undefined;

      const contratoComDetalhes: ContratoWithDetails = {
        ...contrato,
        lead: leadNormalizado,
        proposta
      };

      console.log('✅ Contrato com detalhes encontrado');
      return contratoComDetalhes;
    } catch (error) {
      console.error('❌ Erro no serviço de contratos:', error);
      throw error;
    }
  }

  /**
   * Lista todos os contratos usando autenticação RBAC
   */
  static async listarContratos(): Promise<ContratoWithDetails[]> {
    try {
      console.log('🔍 Listando contratos via API backend com RBAC...');

      // Usar fetchWithAuth para respeitar políticas RBAC
      const response = await fetchWithAuth('/api/contratos', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`Erro ao carregar contratos: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ ${data?.length || 0} contratos carregados com sucesso (filtrados por RBAC)`);
      
      return data as ContratoWithDetails[];
    } catch (error) {
      console.error('❌ Erro ao carregar contratos:', error);
      throw error;
    }
  }

  /**
   * Gera contrato em PDF
   */
  static async gerarContratoPDF(contratoId: string): Promise<string> {
    try {
      console.log('🚀 [CONTRATOS] Gerando PDF do contrato:', contratoId);
      
      const response = await fetchWithAuth('/api/contratos/gerar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contratoId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Erro na API: ${response.status}`);
      }

      const result = await response.json();
      return result.pdfUrl;
    } catch (error) {
      console.error('Erro ao gerar contrato PDF:', error);
      throw error;
    }
  }

  /**
   * Atualiza URL do PDF do contrato
   */
  static async atualizarURLContrato(contratoId: string, pdfUrl: string): Promise<Contrato> {
    try {
      const { data, error } = await supabase
        .from('contratos_sun7')
        .update({
          contrato_pdf_url: pdfUrl,
          status: 'contrato_gerado'
        })
        .eq('id', contratoId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar URL do contrato:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro no serviço de contratos:', error);
      throw error;
    }
  }

  /**
   * Deleta um contrato via API.
   * Se for o único contrato gerado do lead, o backend reverte o status para Lead
   * e a etapa para Análise de Necessidades.
   */
  static async deletarContrato(contratoId: string): Promise<{ leadRevertido?: boolean }> {
    try {
      console.log('🗑️ Deletando contrato:', contratoId);

      const response = await fetchWithAuth(`/api/contratos/${contratoId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `Erro ao deletar contrato: ${response.status}`);
      }

      const data = await response.json().catch(() => ({}));
      console.log('✅ Contrato deletado com sucesso', data.leadRevertido ? '(lead revertido para Lead)' : '');
      return data;
    } catch (error) {
      console.error('❌ Erro no serviço de contratos:', error);
      throw error;
    }
  }

  /**
   * Atualiza o status de um contrato
   */
  static async atualizarStatusContrato(contratoId: string, status: string): Promise<void> {
    try {
      console.log(`🔄 Atualizando status do contrato ${contratoId} para ${status}`);
      
      const response = await fetchWithAuth(`/api/contratos/${contratoId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `Erro ao atualizar status: ${response.status}`);
      }

      console.log('✅ Status do contrato atualizado com sucesso');
    } catch (error) {
      console.error('❌ Erro ao atualizar status do contrato:', error);
      throw error;
    }
  }

  /**
   * Reverte a finalização de um contrato
   */
  static async reverterFinalizacaoContrato(contratoId: string): Promise<void> {
    try {
      console.log('🔄 Revertendo finalização do contrato:', contratoId);
      
      const response = await fetchWithAuth(`/api/contratos/${contratoId}/reverter-finalizacao`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `Erro ao reverter finalização: ${response.status}`);
      }

      console.log('✅ Finalização do contrato revertida com sucesso');
    } catch (error) {
      console.error('❌ Erro ao reverter finalização do contrato:', error);
      throw error;
    }
  }
}
